import { useState, useCallback } from 'react'
import {
  ProcessingStatus,
  SnapshotRow,
  ProcessingResult,
  RunAuditRecord,
} from '../types/borrowerChecker'
import { validateSnapshot } from '../utils/validators'
import { MOCK_INTERNAL_RECORDS, MOCK_RUNNING_USER } from '../utils/mockData'
import { getMissingLoansFilename } from '../utils/csvGenerator'

function parseCSVContent(content: string): SnapshotRow[] {
  const lines = content.split(/\r?\n/).filter((line) => line.trim() !== '')
  if (lines.length < 2) return []

  const headerLine = lines[0]
  const headers = parseCSVLine(headerLine).map((h) => h.trim().toLowerCase())

  const lenderLoanIdIdx = headers.findIndex(
    (h) =>
      h === 'lender loan id' ||
      h === 'lenderloanid' ||
      h === 'lender_loan_id' ||
      h === 'loan id' ||
      h === 'loanid',
  )
  const borrowerNameIdx = headers.findIndex(
    (h) =>
      h === 'borrower name' ||
      h === 'borrowername' ||
      h === 'borrower_name' ||
      h === 'name',
  )
  const loanAmountIdx = headers.findIndex(
    (h) =>
      h === 'loan amount' ||
      h === 'loanamount' ||
      h === 'loan_amount' ||
      h === 'amount',
  )
  const loanDateIdx = headers.findIndex(
    (h) =>
      h === 'loan date' ||
      h === 'loandate' ||
      h === 'loan_date' ||
      h === 'date',
  )
  const propertyAddressIdx = headers.findIndex(
    (h) =>
      h === 'property address' ||
      h === 'propertyaddress' ||
      h === 'property_address' ||
      h === 'address',
  )
  const loanTypeIdx = headers.findIndex(
    (h) =>
      h === 'loan type' ||
      h === 'loantype' ||
      h === 'loan_type' ||
      h === 'type',
  )

  const rows: SnapshotRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const fields = parseCSVLine(lines[i])
    const rawData: Record<string, string> = {}
    headers.forEach((header, idx) => {
      rawData[header] = fields[idx] || ''
    })

    rows.push({
      rowNumber: i + 1,
      lenderLoanId: (lenderLoanIdIdx >= 0
        ? fields[lenderLoanIdIdx]
        : ''
      ).trim(),
      borrowerName: (borrowerNameIdx >= 0
        ? fields[borrowerNameIdx]
        : ''
      ).trim(),
      loanAmount: (loanAmountIdx >= 0 ? fields[loanAmountIdx] : '').trim(),
      loanDate: (loanDateIdx >= 0 ? fields[loanDateIdx] : '').trim(),
      propertyAddress: (propertyAddressIdx >= 0
        ? fields[propertyAddressIdx]
        : ''
      ).trim(),
      loanType: (loanTypeIdx >= 0 ? fields[loanTypeIdx] : '').trim(),
      rawData,
    })
  }

  return rows
}

function parseCSVLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
    } else {
      if (char === '"') {
        inQuotes = true
      } else if (char === ',') {
        fields.push(current)
        current = ''
      } else {
        current += char
      }
    }
  }
  fields.push(current)
  return fields
}

function findDuplicates(rows: SnapshotRow[]): SnapshotRow[] {
  const seen = new Map<string, number>()
  const duplicates: SnapshotRow[] = []

  for (const row of rows) {
    const id = row.lenderLoanId.toUpperCase()
    if (!id) continue
    const count = seen.get(id) || 0
    seen.set(id, count + 1)
    if (count > 0) {
      duplicates.push(row)
    }
  }

  return duplicates
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function useSnapshotProcessor() {
  const [status, setStatus] = useState<ProcessingStatus>(ProcessingStatus.Idle)
  const [result, setResult] = useState<ProcessingResult | null>(null)
  const [auditRecord, setAuditRecord] = useState<RunAuditRecord | null>(null)

  const processFile = useCallback(async (file: File) => {
    const runTimestamp = new Date().toISOString()
    const runningUser = MOCK_RUNNING_USER

    try {
      setStatus(ProcessingStatus.Validating)
      const content = await file.text()
      const parsedRows = parseCSVContent(content)

      if (parsedRows.length === 0) {
        throw new Error(
          'No data rows found in the uploaded file. Please check the file format and try again.',
        )
      }

      await delay(800)

      const { validRows, errors: validationErrors } =
        validateSnapshot(parsedRows)
      const duplicates = findDuplicates(validRows)

      setStatus(ProcessingStatus.Processing)
      await delay(1200)

      const internalIds = new Set(
        MOCK_INTERNAL_RECORDS.map((r) => r.lenderLoanId.toUpperCase()),
      )

      const uniqueValidRows = validRows.filter((row) => {
        const id = row.lenderLoanId.toUpperCase()
        const isDup = duplicates.some((d) => d.rowNumber === row.rowNumber)
        return (
          !isDup ||
          validRows.findIndex((r) => r.lenderLoanId.toUpperCase() === id) ===
            validRows.indexOf(row)
        )
      })

      const matched: SnapshotRow[] = []
      const missing: SnapshotRow[] = []

      for (const row of uniqueValidRows) {
        if (internalIds.has(row.lenderLoanId.toUpperCase())) {
          matched.push(row)
        } else {
          missing.push(row)
        }
      }

      const missingFilename = getMissingLoansFilename(runTimestamp)

      const processingResult: ProcessingResult = {
        totalRows: parsedRows.length,
        validRows: validRows.length,
        matchedCount: matched.length,
        missingCount: missing.length,
        duplicateCount: duplicates.length,
        validationErrorCount: validationErrors.length,
        missingRecords: missing,
        duplicates,
        validationErrors,
        status: 'Success',
        errorMessage: null,
        runTimestamp,
        snapshotFilename: file.name,
        runningUser,
      }

      const audit: RunAuditRecord = {
        runTimestamp,
        runningUserId: runningUser.id,
        runningUserName: runningUser.name,
        runningUserEmail: runningUser.email,
        snapshotFilename: file.name,
        totalRowCount: parsedRows.length,
        validRowCount: validRows.length,
        matchedCount: matched.length,
        missingCount: missing.length,
        duplicateCount: duplicates.length,
        validationErrorCount: validationErrors.length,
        missingReportFilename: missingFilename,
        status: 'Success',
        errorMessage: '',
      }

      setResult(processingResult)
      setAuditRecord(audit)
      setStatus(ProcessingStatus.Success)
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred during processing.'

      const failedResult: ProcessingResult = {
        totalRows: 0,
        validRows: 0,
        matchedCount: 0,
        missingCount: 0,
        duplicateCount: 0,
        validationErrorCount: 0,
        missingRecords: [],
        duplicates: [],
        validationErrors: [],
        status: 'Failed',
        errorMessage,
        runTimestamp,
        snapshotFilename: file.name,
        runningUser,
      }

      const failedAudit: RunAuditRecord = {
        runTimestamp,
        runningUserId: runningUser.id,
        runningUserName: runningUser.name,
        runningUserEmail: runningUser.email,
        snapshotFilename: file.name,
        totalRowCount: 0,
        validRowCount: 0,
        matchedCount: 0,
        missingCount: 0,
        duplicateCount: 0,
        validationErrorCount: 0,
        missingReportFilename: '',
        status: 'Failed',
        errorMessage,
      }

      setResult(failedResult)
      setAuditRecord(failedAudit)
      setStatus(ProcessingStatus.Failed)
    }
  }, [])

  const reset = useCallback(() => {
    setStatus(ProcessingStatus.Idle)
    setResult(null)
    setAuditRecord(null)
  }, [])

  return { status, result, auditRecord, processFile, reset }
}
